import React, { useEffect, useState } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';

import { useReduceMotion } from '../components/Skeleton';

/**
 * Три пляшущих столбика — «это играет сейчас». Стоят в строке текущего трека и вместо ноты на
 * кнопке в шапке: по ним из списка чатов видно, что музыка идёт, и куда нажать, чтобы к ней
 * вернуться.
 *
 * <p>Столбики качаются с разными периодами (взаимно простыми), поэтому рисунок не повторяется
 * и не выглядит механическим. На паузе замирают в низком положении, а не исчезают: знак
 * «здесь есть музыка» остаётся. При «уменьшить движение» — стоят всегда.
 */

const PERIODS = [520, 690, 610];
const LOW = 0.3;

export function NowPlayingBars({
  playing,
  color,
  size = 16,
}: {
  playing: boolean;
  color: string;
  size?: number;
}): React.ReactElement {
  const reduceMotion = useReduceMotion();
  const [values] = useState(() => PERIODS.map(() => new Animated.Value(LOW)));

  useEffect(() => {
    if (!playing || reduceMotion) {
      values.forEach((v, i) => {
        v.stopAnimation();
        Animated.timing(v, {
          toValue: reduceMotion && playing ? [0.6, 1, 0.75][i] : LOW,
          duration: 180,
          useNativeDriver: true,
        }).start();
      });
      return;
    }
    const loops = values.map((v, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(v, { toValue: 1, duration: PERIODS[i], easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
          Animated.timing(v, { toValue: LOW + 0.1 * i, duration: PERIODS[i], easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        ]),
      ),
    );
    loops.forEach((l) => l.start());
    return () => loops.forEach((l) => l.stop());
  }, [playing, reduceMotion, values]);

  const barWidth = Math.max(2, Math.round(size / 5.5));
  return (
    <View style={[styles.row, { width: size, height: size }]} accessibilityElementsHidden importantForAccessibility="no">
      {values.map((v, i) => (
        <Animated.View
          key={i}
          style={{
            width: barWidth,
            height: size,
            borderRadius: barWidth / 2,
            backgroundColor: color,
            transform: [{ translateY: size / 2 }, { scaleY: v }, { translateY: -size / 2 }],
          }}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' },
});
